import { Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  CATEGORY_ORDER,
  getCatalogProducts,
  matchesProductQuery,
  type Product,
} from '../Product/productData';
import constants from '../../constants/Constants';

const RECENT_KEY = 'recent_tools';
const RECENT_LIMIT = 3;
const SWIPE_CLOSE_DISTANCE = 80;

// Storage can throw in a private window. The list then opens without recents.
function readRecent(): string[] {
  try {
    const paths = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    if (Array.isArray(paths)) return paths.filter((path) => typeof path === 'string');
  } catch {}
  return [];
}

function writeRecent(paths: string[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(paths));
  } catch {}
}

type ToolsSwitcherProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReportIssue: () => void;
};

type Section = { label: string; products: Product[] };

export default function ToolsSwitcher({ open, onOpenChange, onReportIssue }: ToolsSwitcherProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStart = useRef<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const products = useMemo(() => getCatalogProducts(), []);

  useEffect(() => {
    const product = products.find(({ to }) => to === pathname || pathname.startsWith(`${to}/`));
    if (!product) return;
    writeRecent([product.to, ...readRecent().filter((path) => path !== product.to)].slice(0, RECENT_LIMIT + 1));
  }, [pathname, products]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setDragOffset(0);
    }
  }, [open]);

  const sections = useMemo<Section[]>(() => {
    const trimmed = query.trim();
    if (trimmed) {
      return [{ label: 'Matches', products: products.filter((product) => matchesProductQuery(product, trimmed)) }];
    }
    const recent = readRecent()
      .filter((path) => path !== pathname)
      .map((path) => products.find(({ to }) => to === path))
      .filter((product): product is Product => Boolean(product))
      .slice(0, RECENT_LIMIT);
    const groups = CATEGORY_ORDER.map((category) => ({
      label: constants.TAB_LABELS[category as keyof typeof constants.TAB_LABELS],
      products: products.filter((product) => product.category === category),
    }));
    if (recent.length > 0) {
      return [{ label: 'Recent', products: recent }, ...groups];
    }
    return groups;
  }, [query, products, pathname, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = sections.flatMap((section) => section.products);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = () => onOpenChange(false);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(current + 1, flat.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      const product = flat[active];
      if (!product) return;
      event.preventDefault();
      if (event.metaKey || event.ctrlKey) {
        window.open(product.to, '_blank', 'noopener');
      } else {
        navigate(product.to);
      }
      close();
    }
  };

  const handleGrabberDown = (event: PointerEvent<HTMLDivElement>) => {
    dragStart.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handleGrabberMove = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStart.current === null) return;
    setDragOffset(Math.max(0, event.clientY - dragStart.current));
  };
  const handleGrabberUp = () => {
    dragStart.current = null;
    if (dragOffset > SWIPE_CLOSE_DISTANCE) {
      close();
    } else {
      setDragOffset(0);
    }
  };

  let rowIndex = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          // A focused field opens the on-screen keyboard over the list on a phone.
          if (window.matchMedia('(pointer: coarse)').matches) event.preventDefault();
        }}
        style={{
          transform: dragOffset ? `translateY(${dragOffset}px)` : undefined,
        }}
        className={cn(
          'gap-0 overflow-hidden border-white/10 bg-[#171717] !p-0 [&>button:last-child]:hidden',
          'md:!top-[12vh] md:!max-h-[76vh] md:h-[76vh] md:w-[600px] md:max-w-[600px] md:!translate-y-0',
          'max-md:!bottom-0 max-md:!left-0 max-md:!top-[max(3rem,env(safe-area-inset-top))] max-md:!max-h-none max-md:w-full max-md:max-w-none max-md:!translate-x-0 max-md:!translate-y-0 max-md:rounded-b-none'
        )}
      >
        <DialogTitle className="sr-only">Tools</DialogTitle>
        <DialogDescription className="sr-only">Search for a tool, then open it.</DialogDescription>
        <div
          className="flex h-6 shrink-0 touch-none items-center justify-center md:hidden"
          onPointerDown={handleGrabberDown}
          onPointerMove={handleGrabberMove}
          onPointerUp={handleGrabberUp}
          onPointerCancel={handleGrabberUp}
        >
          <span aria-hidden className="h-[5px] w-10 rounded-full bg-white/30" />
        </div>
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-white/10 px-4">
          <Search aria-hidden className="size-5 shrink-0 text-white/60" />
          <input
            type="search"
            role="combobox"
            aria-expanded="true"
            aria-controls="tools-list"
            aria-activedescendant={flat[active] ? `tool-${flat[active].to}` : undefined}
            aria-label="Search tools"
            placeholder="Search tools"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={handleKeyDown}
            className="min-w-0 flex-1 bg-transparent text-base text-white outline-none placeholder:text-white/60 focus-visible:outline-none md:text-[1.05rem] [&::-webkit-search-cancel-button]:hidden"
          />
          <button
            type="button"
            onClick={close}
            className="-mr-2 inline-flex h-11 items-center px-2 text-sm font-medium text-primary md:hidden"
          >
            Done
          </button>
          <button
            type="button"
            onClick={close}
            aria-label="Close tools"
            className="-mr-2 hidden size-9 items-center justify-center rounded-md text-white/70 hover:bg-white/5 hover:text-white md:inline-flex"
          >
            <X className="size-4" />
          </button>
        </div>
        <ul id="tools-list" ref={listRef} role="listbox" aria-label="Tools" className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
          {sections.map((section) => (
            <li key={section.label} role="presentation">
              <div className="label-mono px-2.5 pb-1 pt-3">{section.label}</div>
              <ul role="presentation">
                {section.products.map((product) => {
                  rowIndex += 1;
                  const isActive = rowIndex === active;
                  const index = rowIndex;
                  return (
                    <li key={`${section.label}-${product.to}`} role="presentation">
                      <Link
                        id={`tool-${product.to}`}
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive}
                        to={product.to}
                        onClick={close}
                        onMouseMove={() => setActive(index)}
                        className={cn(
                          'flex min-h-11 items-center gap-3 rounded-lg px-2.5 py-1.5 text-white no-underline outline-none',
                          isActive && 'bg-primary'
                        )}
                      >
                        <span aria-hidden className="w-6 shrink-0 text-center text-lg leading-none">{product.icon}</span>
                        <span className="min-w-0 flex-1 truncate text-[0.95rem] font-medium">{product.primary}</span>
                        <span className={cn('shrink-0 font-mono text-xs', isActive ? 'text-white' : 'text-white/60')}>{product.to}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
          {flat.length === 0 ? (
            <li role="presentation" className="px-2.5 py-6 text-center text-sm text-muted-foreground">
              No tool matches "{query.trim()}".
            </li>
          ) : null}
        </ul>
        <div className="flex shrink-0 items-center justify-between border-t border-white/10 px-2 py-1 pb-[max(4px,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onReportIssue}
            className="inline-flex min-h-11 items-center rounded-md px-2.5 text-sm font-medium text-white/80 hover:text-white md:min-h-9"
          >
            Report an Issue on This Page
          </button>
          <span className="hidden items-center gap-3 pr-2 font-mono text-xs text-white/60 md:flex">
            <span>↑↓ Move</span>
            <span>↵ Open</span>
            <span>⌘↵ New Tab</span>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
